
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import Card from '@/components/ui-elements/card/Card.vue';
import CardContent from '@/components/ui-elements/card/CardContent.vue';
import CardHeader from '@/components/ui-elements/card/CardHeader.vue';
import LineChart from '@/components/charts/LineChart.vue';
import ReportsEmpty from '@/components/partials/ReportsEmpty.vue';
import ReportsForm from '@/components/partials/ReportsForm.vue';
import ReportsSkeleton from '@/components/partials/ReportsSkeleton.vue';
import ReportsSummary from '@/components/partials/ReportsSummary.vue';
import ReportsTable from '@/components/tables/ReportsTable.vue';
import useChart, { ChartData } from '@/hooks/useChart';
import useCurrency from '@/hooks/useCurrency';
import useHttp from '@/hooks/useHttp';
import useTimestamp from '@/hooks/useTimestamp';

export default defineComponent({
	components: {
		Card,
		CardContent,
		CardHeader,
		LineChart,
		ReportsEmpty,
		ReportsForm,
		ReportsSkeleton,
		ReportsSummary,
		ReportsTable,
	},
	setup() {
		const { getChartData } = useChart();
		const { formatDollar } = useCurrency();
		const { postAuth, getDataFromResponse } = useHttp();
		const { formatDate } = useTimestamp();

		const averageBalance = ref('0.00');
		const endBalance = reactive({
			amount: '',
			month: '',
		});
		const highestBalance = reactive({
			amount: '',
			month: '',
		});
		const lowestBalance = reactive({
			amount: '',
			month: '',
		});
		const startBalance = reactive({
			amount: '',
			month: '',
		});
		const ytdBalance = reactive({
			amount: '0.00',
			percent: '0',
		});
		const chartData: ChartData = reactive({
			labels: [],
			datasets: [],
		});
		const type = ref('');
		const hasSearched = ref(false);
		const isLoading = ref(false);
		const searchResults = ref([]);
		const summaryData: Record<string, number> = reactive({});
		const year = ref('');

		const ytdValue = computed(() => {
			if (type.value === 'incomes') {
				return 'Earned';
			} else if (['banks', 'investments'].includes(type.value)) {
				if (+ytdBalance.percent < 0) {
					return 'Loss';
				}
				return 'Saved';
			} else {
				return 'Spent';
			}
		});

		const runSearch = async (params: Record<string, string>) => {
			isLoading.value = true;
			type.value = params.billType;
			const data = {
				path: 'api/search',
				params,
			};
			const response = await postAuth(data);

			if (response.success) {
				const result = getDataFromResponse(response);
				year.value = params.year;
				searchResults.value = result;
				const { labels, datasets } = getChartData(
					result,
					params.billType
				);
				chartData.labels = labels;
				chartData.datasets = datasets;
			}

			hasSearched.value = true;
			isLoading.value = false;
		};

		const setSummary = (total: number, cycle: string) => {
			summaryData[formatDate('MMMM', cycle)] = total;
		};

		watch(summaryData, summary => {
			const months = Object.keys(summary);
			const dollars = Object.values(summary);
			const maxDollars = Math.max(...dollars);
			const minDollars = Math.min(...dollars);

			averageBalance.value = formatDollar(
				(dollars.reduce((a, c) => a + c, 0) / dollars.length).toString()
			);
			endBalance.amount = formatDollar(dollars[dollars.length - 1]);
			endBalance.month = months[months.length - 1];
			highestBalance.amount = formatDollar(maxDollars);
			highestBalance.month = months[dollars.indexOf(maxDollars)];
			lowestBalance.amount = formatDollar(minDollars);
			lowestBalance.month = months[dollars.indexOf(minDollars)];
			startBalance.amount = formatDollar(dollars[0]);
			startBalance.month = months[0];
			ytdBalance.percent = Math.round(
				((dollars[dollars.length - 1] - dollars[0]) / dollars[0]) * 100
			).toString();

			if (['banks', 'investments'].includes(type.value)) {
				ytdBalance.amount = formatDollar(
					dollars[dollars.length - 1] - dollars[0]
				);
			} else {
				ytdBalance.amount = formatDollar(
					dollars.reduce((a, c) => a + c, 0)
				);
			}
		});

		return {
			averageBalance,
			chartData,
			endBalance,
			hasSearched,
			highestBalance,
			lowestBalance,
			isLoading,
			runSearch,
			searchResults,
			setSummary,
			startBalance,
			type,
			year,
			ytdBalance,
			ytdValue,
		};
	},
});
